<!-- Right-side quick-view/quick-edit panel for a BPMN process element (task,
     event, gateway or flow), opened by clicking that element on the process
     diagram instead of navigating straight to its full element page.

     The domain-object counterpart is DomainObjectDrawer.vue, and this panel is
     deliberately its twin: same 420px in-layout column, same "the app-wide
     Edit/View toggle decides which mode shows" rule, same "View full details"
     escape hatch. Two differences, both forced rather than chosen:

       - NO delete. The full element pages only offer delete for an ARCHIVED
         element, and an archived element is by definition no longer on the
         diagram — so there is nothing here a delete button could apply to.
         Removing an element from a process means deleting its shape on the
         canvas, which is where that action already lives.

       - The NAME is written to the BPMN, not to the database. A process
         element's name lives in the diagram: sync_elements() re-stamps
         process_element.name from the BPMN's `name` attribute on every
         definition sync, and bpmn-js draws the label from the same attribute.
         An update_task(name=…) would move neither, and would be reverted by
         the next canvas edit. So renames go through the canvas handle on
         process-element-drawer-store, and only where a modeler is attached
         (edit mode) — which is also the only mode that offers the field.

     Like DomainObjectDrawer, the description is NOT taken from the element
     list's `short_description` (a lossy ~256-char, markdown-stripped server
     preview — saving it back would replace the real description with its own
     shadow); it is fetched through the artefact link the list already carries.
     Description saves go through syncStore.queueSync for the retries, Sentry
     capture, failed-sync recovery UI and cross-tab invalidation a bare api.*
     call would drop.

     Scope is deliberately name + description only. Linked domain objects are
     edited from the full element page, where BusinessEntityRefs has the room
     for its relationship/target pair and the upstream-action chain that gives
     those rows their meaning. -->
<template>
    <Transition name="element-panel" :css="drawerOpen || drawerLeaveAnimated">
        <aside
            v-if="drawerOpen"
            ref="panelRef"
            class="element-panel"
            data-testid="process-element-panel"
            tabindex="-1"
            @keydown.esc="closeDrawer"
        >
            <div class="element-panel-header">
                <div class="element-panel-heading">
                    <span class="element-panel-eyebrow">{{ typeLabel }}</span>
                    <span class="element-panel-title">{{
                        displayName || "Unnamed"
                    }}</span>
                </div>
                <Button
                    icon="pi pi-times"
                    text
                    rounded
                    size="small"
                    aria-label="Close panel"
                    @click="closeDrawer"
                />
            </div>

            <div class="element-panel-body">
                <!-- Terminal state: the id resolves against the CURRENT
                     process's element list, so "not in the list and nothing in
                     flight" means it is not coming. Deliberately not a spinner
                     — an unbounded spinner is a promise the panel can't keep. -->
                <template v-if="elementUnavailable">
                    <div
                        class="panel-unavailable"
                        data-testid="panel-unavailable"
                    >
                        <i class="pi pi-exclamation-circle" />
                        <p>
                            This element isn't part of the process any more. It
                            may have been removed from the diagram.
                        </p>
                        <Button
                            label="Close"
                            size="small"
                            outlined
                            data-testid="panel-unavailable-close"
                            @click="closeDrawer"
                        />
                    </div>
                </template>

                <template v-else-if="!processElement">
                    <div class="panel-empty">
                        <ProgressSpinner />
                    </div>
                </template>

                <template v-else-if="isEditing">
                    <div class="panel-form">
                        <!-- Only offered where a modeler is attached: without
                             one there is nothing that could move the label, and
                             an input that silently discards what is typed into
                             it is worse than no input. -->
                        <div v-if="canRename" class="field">
                            <label for="panel-element-name">Name</label>
                            <InputText
                                id="panel-element-name"
                                v-model="formName"
                                size="small"
                                class="w-full"
                                :invalid="!!nameError"
                                aria-describedby="panel-element-name-error"
                                data-testid="panel-element-name"
                                @input="onNameInput"
                                @blur="commitName"
                                @keydown.enter="commitName"
                            />
                            <!-- A field-level problem: it holds back only the
                                 rename, never the description edit beside
                                 it. -->
                            <small
                                v-if="nameError"
                                id="panel-element-name-error"
                                class="field-error"
                                data-testid="panel-name-error"
                                >{{ nameError }}</small
                            >
                        </div>

                        <div class="field">
                            <label for="panel-element-description"
                                >Description</label
                            >
                            <div
                                v-if="descriptionState === 'loading'"
                                class="field-loading"
                                data-testid="panel-description-loading"
                            >
                                <ProgressSpinner
                                    style="width: 1.5rem; height: 1.5rem"
                                />
                                <span>Loading description…</span>
                            </div>
                            <div
                                v-else-if="descriptionState === 'error'"
                                class="field-error-block"
                                data-testid="panel-description-error"
                            >
                                <p>
                                    The description couldn't be loaded, so it
                                    isn't editable here — editing it now would
                                    risk overwriting it.
                                </p>
                                <Button
                                    label="Retry"
                                    size="small"
                                    text
                                    data-testid="panel-description-retry"
                                    @click="loadDescription"
                                />
                            </div>
                            <Textarea
                                v-else
                                id="panel-element-description"
                                v-model="formDescription"
                                rows="5"
                                size="small"
                                class="w-full"
                                data-testid="panel-element-description"
                            />
                        </div>

                        <p class="panel-form-hint">
                            Linked domain objects, documents, compliance
                            evidence and other details can be edited from the
                            full page.
                        </p>
                    </div>
                </template>

                <template v-else>
                    <div class="panel-summary">
                        <div class="summary-section">
                            <h3 class="section-title">Description</h3>
                            <div
                                v-if="descriptionState === 'loading'"
                                class="field-loading"
                            >
                                <ProgressSpinner
                                    style="width: 1.5rem; height: 1.5rem"
                                />
                            </div>
                            <!-- Rendered from the real markdown, not
                                 `short_description`. `html: false` on the
                                 MarkdownIt instance escapes any raw HTML in the
                                 source, so v-html cannot inject markup here. -->
                            <div
                                v-else-if="renderedDescription"
                                class="summary-desc markdown-body"
                                data-testid="panel-description"
                                v-html="renderedDescription"
                            />
                            <p
                                v-else-if="descriptionState === 'error'"
                                class="summary-desc muted"
                            >
                                The description couldn't be loaded.
                            </p>
                            <p v-else class="summary-desc muted">
                                No description yet.
                            </p>
                        </div>

                        <!-- Read-only on purpose. Marking a step as needing
                             proof and writing that proof's spec is one
                             decision, so splitting it across two surfaces
                             would be worse than keeping it whole on the full
                             page — and the drawer stays out of
                             update_task_evidence, which takes both fields
                             together and has a history of partial writes
                             racing each other (see task-store). Reading it
                             here is the half that genuinely belongs on the
                             canvas: walking the diagram to see which steps
                             have to produce evidence, and what. -->
                        <div v-if="showEvidence" class="summary-section">
                            <h3 class="section-title">Compliance evidence</h3>
                            <p class="summary-value evidence-required">
                                <i class="pi pi-shield" />
                                This task generates evidence
                            </p>
                            <div
                                v-if="evidenceState === 'loading'"
                                class="field-loading"
                            >
                                <ProgressSpinner
                                    style="width: 1.5rem; height: 1.5rem"
                                />
                            </div>
                            <!-- Full text, not the ~256-char stripped preview
                                 the hover card shows: being able to read the
                                 whole spec without leaving the diagram is the
                                 point of showing it here at all. -->
                            <div
                                v-else-if="renderedEvidence"
                                class="summary-desc markdown-body"
                                data-testid="panel-evidence-instructions"
                                v-html="renderedEvidence"
                            />
                            <p
                                v-else-if="evidenceState === 'error'"
                                class="summary-desc muted"
                            >
                                The evidence instructions couldn't be loaded.
                            </p>
                            <p v-else class="summary-desc muted">
                                No evidence instructions yet.
                            </p>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Pinned rather than the last child of the scrolling body, so it
                 stays reachable however long the description runs. No delete
                 here: see the file header. -->
            <div v-if="!elementUnavailable" class="element-panel-footer">
                <Button
                    icon="pi pi-window-maximize"
                    label="View full details"
                    text
                    size="small"
                    data-testid="panel-open-full-page"
                    @click="openFullPage"
                />
            </div>
        </aside>
    </Transition>
</template>

<script setup lang="ts">
import { throttle } from "lodash-es";
import MarkdownIt from "markdown-it";
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import Textarea from "primevue/textarea";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useLayerVisibility } from "@/composables/bpmn-manager/layer";
import { useComplianceEntitlement } from "@/composables/compliance-entitlement";
import { FILE_SYNC_THROTTLE_MS } from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { registerFlush, unregisterFlush } from "@/composables/pending-flush";
import {
    isAbortError,
    useLatestRequest,
} from "@/composables/use-latest-request";
import { ProcessElementType, type ProcessTask } from "@/lib/openapi/gen";
import { followLink } from "@/lib/openapi/helpers";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useProcessElementDrawerStore } from "@/stores/process-element-drawer-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

/**
 * Everything that differs per element type: which entity the sync pipeline
 * files the write under, which id key that endpoint's body expects, and its
 * url. Kept as one table rather than four branches so a new element type
 * cannot be half-added.
 */
const SYNC_TARGETS = {
    [ProcessElementType.TASK]: {
        entityType: EntityType.TASK,
        idKey: "task_id",
        url: "/process/process_element_mgt/update_task/start",
        label: "Task",
    },
    [ProcessElementType.GATEWAY]: {
        entityType: EntityType.GATEWAY,
        idKey: "gateway_id",
        url: "/process/process_element_mgt/update_gateway/start",
        label: "Gateway",
    },
    [ProcessElementType.EVENT]: {
        entityType: EntityType.EVENT,
        idKey: "event_id",
        url: "/process/process_element_mgt/update_event/start",
        label: "Event",
    },
    [ProcessElementType.FLOW]: {
        entityType: EntityType.FLOW,
        idKey: "flow_id",
        url: "/process/process_element_mgt/update_flow/start",
        label: "Flow",
    },
} as const;

const route = useRoute();
const router = useRouter();

const drawerStore = useProcessElementDrawerStore();
const {
    drawerOpen,
    drawerElementId,
    drawerBpmnId,
    drawerElementType,
    drawerElementName,
    drawerLeaveAnimated,
    canRename,
} = storeToRefs(drawerStore);
const { closeDrawer, renameElement } = drawerStore;

const syncStore = useSyncStore();
const releaseStore = useReleaseStore();
const { process } = useCurrentEntityManager();
const { processElements, isFetching } = useProcessElementsManager();
const { isEditing } = useEditManager();
const { complianceEnabled } = useComplianceEntitlement();
const layerVisibility = useLayerVisibility();

const panelRef = ref<HTMLElement | null>(null);

/**
 * The element this panel is about.
 *
 * Resolved by ProcessElement identity first and by bpmn_id second: an element
 * added to the diagram in this session carries no processElementId in the XML
 * until the definition sync stamps it, and the canvas opens the drawer on
 * whichever of the two it has (see Definition.vue).
 */
const processElement = computed(() => {
    const elements = processElements.value;
    if (!elements) return undefined;
    return (
        elements.find((e) => e.identity === drawerElementId.value) ??
        elements.find(
            (e) => !!drawerBpmnId.value && e.bpmn_id === drawerBpmnId.value
        )
    );
});

/** "Asked for, not present, and nothing in flight that could still produce it." */
const elementUnavailable = computed(
    () =>
        !!drawerElementId.value &&
        !processElement.value &&
        !isFetching.value &&
        (processElements.value?.length ?? 0) > 0
);

/**
 * The element's type, preferring the server's over the one captured when the
 * shape was clicked.
 *
 * This drives which endpoint the description is written to and under which id
 * key, so the two must not be allowed to diverge: the panel deliberately
 * outlives diagram reloads (it is keyed on processElementId), and a shape whose
 * type changed under it would otherwise POST e.g. `task_id` to update_task for
 * something the server now holds as an event. The click-time type stays as the
 * fallback for an element not yet in the list.
 */
const elementType = computed(
    () => processElement.value?.type ?? drawerElementType.value
);

const syncTarget = computed(() =>
    elementType.value ? SYNC_TARGETS[elementType.value] : undefined
);
const typeLabel = computed(() => syncTarget.value?.label ?? "Element");

/** The header's name: the BPMN's, which leads the store's by a whole sync. */
const displayName = computed(
    () => drawerElementName.value || processElement.value?.name || ""
);

// ---------------------------------------------------------------------------
// Name — written to the BPMN, not to the database (see the file header).
// ---------------------------------------------------------------------------

const formName = ref("");

/**
 * The value last handed to the canvas, so the write-back watch below can tell
 * "the canvas is echoing our own rename" from "somebody renamed this element
 * on the diagram while the panel was open".
 */
const lastCommittedName = ref("");

/**
 * Whether the user has edited the name field since this element was opened.
 *
 * An unnamed element is perfectly legal BPMN — gateways and intermediate
 * events routinely carry no label — so opening the panel on one must not
 * greet the user with a validation error about a name they never touched.
 * The message only means something once they have actually emptied the field.
 */
const nameTouched = ref(false);

const nameError = computed(() =>
    isEditing.value &&
    canRename.value &&
    nameTouched.value &&
    !formName.value.trim()
        ? "Name can't be emptied here — clear it on the diagram instead."
        : ""
);

/**
 * The element `formName` was populated for, so a commit that runs after the
 * store has already moved on still renames the element the text was typed
 * against rather than its successor.
 */
const formNameBpmnId = ref<string | undefined>(undefined);

const onNameInput = () => {
    nameTouched.value = true;
};

/**
 * Commit on blur/Enter rather than per keystroke, unlike this panel's
 * description and unlike DomainObjectDrawer's every field.
 *
 * Every rename is an undoable modeling command: throttling per keystroke would
 * push one command-stack entry per character, so a single Ctrl+Z after typing a
 * name would walk back through it letter by letter instead of undoing "the
 * rename".
 *
 * Blur alone is not enough to make that safe, which is why every close/switch/
 * unmount path calls this too, and why it rides the pending-flush registry:
 * removing a focused input does not reliably fire blur, so Esc, a programmatic
 * close, or edit mode ending from elsewhere (the cross-tab editChannel, the
 * isViewOnly watcher, the history-version watcher) would otherwise drop the
 * rename while the description beside it survived on its own flush.
 *
 * Diffed against `lastCommittedName` rather than the element's current name:
 * that baseline is still valid once the store has moved to another element,
 * and it already tracks both the populated and the committed value.
 */
const commitName = () => {
    const trimmed = formName.value.trim();
    const bpmnId = formNameBpmnId.value;
    if (!trimmed || !bpmnId || !canRename.value) return;
    if (trimmed === lastCommittedName.value) return;
    lastCommittedName.value = trimmed;
    renameElement(trimmed, bpmnId);
};

// ---------------------------------------------------------------------------
// Description: the real markdown, fetched through the artefact link.
// ---------------------------------------------------------------------------

const formDescription = ref("");

/** The baseline the form is diffed against. NEVER seeded from
 *  `short_description` — that value is a lossy preview, and writing it back
 *  would truncate the real description to ~256 chars of plain text. */
const savedDescription = ref("");
const descriptionState = ref<"empty" | "loading" | "ready" | "error">("empty");
const descriptionRequest = useLatestRequest();

/**
 * Text queued for an element but not yet echoed back by the server, keyed by
 * ProcessElement identity.
 *
 * Switching elements re-reads the description from the server, and that read
 * runs behind the user: the save waits out sync-store's inactivity debounce,
 * so coming back within a few seconds fetches the pre-edit text — or, for an
 * element whose description artefact the server has still to mint, nothing at
 * all. The panel already holds what was typed, so it stands in until the
 * server agrees, the same optimistic-until-confirmed arrangement
 * canvas-save-status makes for the entities canvas.
 *
 * Plain Map, not a ref: it is scoped to this panel instance, read only inside
 * loadDescription, and nothing renders off it directly.
 */
const pendingDescriptions = new Map<string, string>();

const md = new MarkdownIt({ html: false, linkify: true });
const renderedDescription = computed(() =>
    descriptionState.value === "ready" && savedDescription.value.trim()
        ? md.render(savedDescription.value)
        : ""
);

const revisionQuery = computed(() =>
    releaseStore.activeRevision
        ? new URLSearchParams({ revision: releaseStore.activeRevision })
        : undefined
);

const loadDescription = async () => {
    const target = processElement.value;
    if (!target) return;

    // Show what was typed straight away, with no spinner: the fetch below can
    // only tell us something older until the save lands.
    const pending = pendingDescriptions.get(target.identity);
    if (pending !== undefined) {
        savedDescription.value = pending;
        formDescription.value = pending;
        descriptionState.value = "ready";
    }

    // An element with no description artefact has nothing to fetch — that is a
    // legitimately empty description, not an error. (Not yet true of one whose
    // artefact the server is still minting, which is what `pending` covers.)
    if (!target.description) {
        if (pending === undefined) {
            savedDescription.value = "";
            formDescription.value = "";
            descriptionState.value = "ready";
        }
        return;
    }

    const { token, signal } = descriptionRequest.begin();
    if (pending === undefined) descriptionState.value = "loading";
    try {
        const { response, data } = await followLink<File | string>(
            target.description,
            "get",
            undefined,
            signal,
            revisionQuery.value
        );

        // A newer request superseded us — commit nothing.
        if (!descriptionRequest.isCurrent(token)) return;

        if (!response.ok) {
            // A stand-in is still the best thing to show; the failed-sync UI
            // owns telling the user about the write itself.
            if (pending === undefined) descriptionState.value = "error";
            return;
        }
        // followLink's `data` is not reliably a Blob — depending on how the
        // client parses the response it can come back as a plain string (see
        // the same defensive handling in fetchMarkdownArtefact).
        const text =
            data instanceof Blob
                ? await data.text()
                : typeof data === "string"
                  ? data
                  : "";
        if (!descriptionRequest.isCurrent(token)) return;

        if (pending !== undefined) {
            // Once the server echoes the queued text back, it owns the value
            // again and the stand-in retires. Until then it keeps standing,
            // rather than replacing what the user typed with what preceded it.
            if (text === pending) {
                pendingDescriptions.delete(target.identity);
            }
            return;
        }

        savedDescription.value = text;
        formDescription.value = text;
        descriptionState.value = "ready";
    } catch (e) {
        if (isAbortError(e)) return;
        if (pending === undefined && descriptionRequest.isCurrent(token)) {
            descriptionState.value = "error";
        }
    }
};

// ---------------------------------------------------------------------------
// Compliance evidence — read-only (see the template for why).
// ---------------------------------------------------------------------------

/**
 * Only tasks carry evidence: generates_evidence and evidence_instructions live
 * on ProcessTask alone, which is why the canvas's own compliancy layer casts
 * before reading them. Gated on the subscription entitlement AND the
 * compliancy layer toggle, matching TaskPage — turning the layer off should
 * quiet this everywhere, not just on the diagram.
 */
const evidenceTask = computed(() =>
    elementType.value === ProcessElementType.TASK
        ? (processElement.value as ProcessTask | undefined)
        : undefined
);

const showEvidence = computed(
    () =>
        complianceEnabled.value &&
        layerVisibility.value.compliancy &&
        evidenceTask.value?.generates_evidence === true
);

const evidenceText = ref("");
const evidenceState = ref<"empty" | "loading" | "ready" | "error">("empty");
const evidenceRequest = useLatestRequest();

const renderedEvidence = computed(() =>
    evidenceState.value === "ready" && evidenceText.value.trim()
        ? md.render(evidenceText.value)
        : ""
);

const loadEvidence = async () => {
    const artefact = evidenceTask.value?.evidence_instructions;
    if (!artefact) {
        evidenceText.value = "";
        evidenceState.value = "ready";
        return;
    }

    const { token, signal } = evidenceRequest.begin();
    evidenceState.value = "loading";
    try {
        const { response, data } = await followLink<File | string>(
            artefact,
            "get",
            undefined,
            signal,
            revisionQuery.value
        );
        if (!evidenceRequest.isCurrent(token)) return;
        if (!response.ok) {
            evidenceState.value = "error";
            return;
        }
        const text =
            data instanceof Blob
                ? await data.text()
                : typeof data === "string"
                  ? data
                  : "";
        if (!evidenceRequest.isCurrent(token)) return;
        evidenceText.value = text;
        evidenceState.value = "ready";
    } catch (e) {
        if (isAbortError(e)) return;
        if (evidenceRequest.isCurrent(token)) evidenceState.value = "error";
    }
};

// Fetch once the section is actually shown, and re-fetch when the element (or
// the artefact behind it) changes. Not folded into the element watch below:
// `generates_evidence` can flip on a later refetch without the element
// changing, and the section must not sit on a stale spinner when it does.
watch(
    [showEvidence, () => evidenceTask.value?.evidence_instructions?.identity],
    ([visible]) => {
        if (!visible) {
            evidenceText.value = "";
            evidenceState.value = "empty";
            return;
        }
        void loadEvidence();
    },
    { immediate: true }
);

// ---------------------------------------------------------------------------
// Saving the description through the app-wide sync pipeline.
// ---------------------------------------------------------------------------

interface QueuedDescriptionSave {
    /** ProcessElement identity, for keying the pending stand-in. */
    elementId: string;
    entityType: EntityType;
    body: Record<string, string | File>;
    url: string;
    descriptionText: string;
}

/**
 * Builds a changed-fields-only payload. Returning undefined means "nothing
 * worth persisting" — the description is applied as a full replacement
 * server-side, so it must never be sent unchanged.
 *
 * Only ever sends a description we actually loaded: while it is loading or
 * errored, formDescription holds no trustworthy baseline and sending it could
 * overwrite real content with an empty or stale value.
 */
const buildPayload = (): QueuedDescriptionSave | undefined => {
    const target = processElement.value;
    const sync = syncTarget.value;
    if (!target || !sync) return undefined;
    if (descriptionState.value !== "ready") return undefined;
    if (formDescription.value === savedDescription.value) return undefined;

    return {
        elementId: target.identity,
        entityType: sync.entityType,
        url: sync.url,
        descriptionText: formDescription.value,
        body: {
            [sync.idKey]: target.identity,
            description: new File([formDescription.value], "description.md", {
                type: "text/markdown",
            }),
        },
    };
};

/**
 * Not awaited and not error-handled here on purpose: queueSync owns retries,
 * Sentry reporting and the failed-sync recovery UI, and process-elements-store
 * refreshes itself off the sync action, so there is no manual refetch — one
 * would tear down and re-centre the diagram on every keystroke.
 */
const performSave = (payload: QueuedDescriptionSave) => {
    // Stands in for the server's answer until the write lands and is echoed
    // back — see pendingDescriptions.
    pendingDescriptions.set(payload.elementId, payload.descriptionText);
    syncStore.queueSync({
        entityType: payload.entityType,
        args: [{ body: payload.body, url: payload.url }],
    });
    // Advance the baseline to what we just queued, mirroring the full pages'
    // `description.persist()` after their own queueSync.
    savedDescription.value = payload.descriptionText;
};

const throttledSave = throttle(performSave, FILE_SYNC_THROTTLE_MS);

// Stable reference — pending-flush keeps callbacks in a module-level Set that
// outlives this component, so it must be the same function object to
// unregister (an inline arrow would leak one dead closure per mount).
//
// Both fields: a name typed but never blurred is as much a pending edit as a
// throttled description. stopEditing() flushes before it clears `isEditing`,
// so the modeler is still registered and Definition.vue's artefact write is
// still open when this runs.
const flushPendingSave = () => {
    commitName();
    throttledSave.flush();
};
registerFlush(flushPendingSave);

onUnmounted(() => {
    // Flush rather than cancel: an unmount must not silently discard a
    // keystroke the user already made. Same for the name, which has its own
    // commit rather than a throttled save.
    commitName();
    throttledSave.flush();
    unregisterFlush(flushPendingSave);
});

// Watching the text itself, so EVERY change reschedules the save with a fresh
// snapshot. Watching a dirty boolean instead would fire once (it latches true
// and stays there) and a later flush would replay that first stale value.
watch(formDescription, () => {
    if (!isEditing.value) return;
    const payload = buildPayload();
    if (payload) throttledSave(payload);
});

// ---------------------------------------------------------------------------
// Element lifecycle
// ---------------------------------------------------------------------------

/**
 * Populate when a different element is opened, and fetch its real description.
 * `immediate: true` also covers the panel already being open when this
 * component (re)mounts.
 */
watch(
    [drawerElementId, drawerBpmnId],
    (_next, previous) => {
        // Must run BEFORE the new element's values are read in. Both carry
        // the OLD element with them: the queued description save captured its
        // id and text when it was scheduled, and commitName renames by
        // `formNameBpmnId` rather than whatever the store now points at.
        if (previous) {
            commitName();
            throttledSave.flush();
        }

        // Reset description state so the new element never shows, or saves,
        // the previous one's text.
        savedDescription.value = "";
        formDescription.value = "";
        descriptionState.value = drawerElementId.value ? "loading" : "empty";

        formName.value = displayName.value;
        formNameBpmnId.value = drawerBpmnId.value;
        lastCommittedName.value = formName.value;
        nameTouched.value = false;

        if (!drawerElementId.value && !drawerBpmnId.value) return;
        void loadDescription();
    },
    { immediate: true }
);

// The element may arrive after the drawer opened (list still loading), or its
// description artefact may appear on a later refetch — fetch once it does.
watch(
    () => processElement.value?.identity,
    (identity) => {
        if (!identity) return;
        if (descriptionState.value === "empty") void loadDescription();
    }
);

/**
 * Follow a rename made on the DIAGRAM while this panel is open (inline
 * direct-editing, or an undo), but never overwrite what the user is typing
 * here.
 *
 * "The user has nothing pending" is decided by comparing the field against the
 * last value this panel itself committed — the same shape of guard
 * DomainObjectDrawer uses for relationships. If the field still holds that,
 * nothing was typed since, so it is safe to move it on; otherwise the user has
 * unsaved work and theirs wins.
 */
watch(drawerElementName, (name) => {
    if (formName.value === lastCommittedName.value) {
        formName.value = name;
        lastCommittedName.value = name;
    }
});

// Populate the name field when the header's Edit/View toggle switches into
// edit mode while the panel is already open.
watch(isEditing, (editing) => {
    if (editing) {
        formName.value = displayName.value;
        lastCommittedName.value = formName.value;
        nameTouched.value = false;
    }
});

/**
 * The page this panel was opened on. Seeded from the current route rather than
 * left empty, because the open state is app-wide while this component is not:
 * it can be mounted with the panel already open.
 */
const openedOnRoute = ref(drawerOpen.value ? String(route.name) : undefined);

// Commit and flush on EVERY close path, not just the close button: the panel
// is also closed by Esc (which unmounts the focused input without a reliable
// blur) and by useExclusiveRightDrawers (opening the Threads drawer), both of
// which would otherwise drop a pending edit.
watch(drawerOpen, (open) => {
    if (!open) {
        commitName();
        throttledSave.flush();
        return;
    }
    openedOnRoute.value = String(route.name);
    void nextTick(() => panelRef.value?.focus());
});

// The panel's data is process-scoped, but its open state is app-wide. Without
// this, navigating to another process leaves a 420px panel pinned open on an
// element that is no longer in the list.
watch(
    () => process.value?.identity,
    (next, previous) => {
        if (previous !== undefined && next !== previous && drawerOpen.value) {
            closeDrawer();
        }
    }
);

// The process watcher above only catches switching to a DIFFERENT process — it
// does nothing when navigating to some other page within the same one, so the
// panel would otherwise stay pinned open on a page it doesn't belong on.
watch(
    () => route.name,
    (name) => {
        if (drawerOpen.value && String(name) !== openedOnRoute.value) {
            closeDrawer();
        }
    }
);

const openFullPage = () => {
    const target = processElement.value;
    const type = elementType.value;
    if (!target || !type) return;
    closeDrawer();
    router.push(
        withThreadRevision({
            name: "processElementPage",
            params: {
                orgName: route.params.orgName,
                stekName: route.params.stekName,
                elementId: target.identity,
                elementType: type,
            },
        })
    );
};
</script>

<style scoped>
/* Same in-layout right-column pattern as DomainObjectDrawer.vue's
   .entity-panel and ThreadDrawer.vue's .thread-panel: a sibling of the
   scrolling .main-container inside the layout's .content-row, so it stretches
   to exactly the space left under this mode's bars. */
.element-panel {
    flex: 0 0 420px;
    width: 420px;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--p-content-background);
    border-left: 1px solid var(--surface-border, #dee2e6);
}

.element-panel:focus {
    outline: none;
}

.element-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 3rem;
    padding: 0.5rem 0.75rem 0.5rem 1rem;
    border-bottom: 1px solid var(--p-surface-200);
}

/* A flex item's automatic minimum size in a row is its content width, so
   without min-width a long element name refuses to shrink and pushes the close
   button past the panel's edge. */
.element-panel-heading {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

/* The element's type, which the diagram conveys by shape and this panel
   otherwise wouldn't: a "Check stock" that is an event reads very differently
   from one that is a task. */
.element-panel-eyebrow {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--p-text-muted-color);
}

.element-panel-title {
    font-weight: 600;
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.element-panel-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: var(--default-gap);
    gap: var(--default-gap);
}

.element-panel-enter-active,
.element-panel-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}

.element-panel-enter-from,
.element-panel-leave-to {
    opacity: 0;
    transform: translateX(16px);
}

/* A sibling of the scrolling body rather than its last child, so it stays
   pinned to the bottom however long the description is. Unlike
   DomainObjectDrawer's footer this holds a single action, so it is left-
   aligned rather than split with `space-between` — there is no destructive
   action to push to the far end. */
.element-panel-footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem var(--default-gap);
    border-top: 1px solid var(--p-surface-200);
    background: var(--p-content-background);
}

.panel-empty {
    display: flex;
    justify-content: center;
    padding: 3rem 0;
}

/* Terminal "this isn't here any more" state — deliberately not a spinner. */
.panel-unavailable {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2.5rem 1rem;
    text-align: center;
    color: var(--p-text-muted-color);
}

.panel-unavailable i {
    font-size: 1.5rem;
}

.panel-unavailable p {
    margin: 0;
    font-size: 0.875rem;
}

.panel-summary,
.panel-form {
    display: flex;
    flex-direction: column;
    gap: var(--default-gap);
}

.summary-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

/* No `white-space: pre-wrap`: the description is rendered markdown, so block
   layout comes from the <p>/<ul>/<h*> elements. Preserving the source's
   newlines on top of that would add a stray break after every wrapped line. */
.summary-desc {
    margin: 0;
    font-size: 0.875rem;
    color: var(--p-text-secondary-color);
}

.summary-desc.muted {
    opacity: 0.7;
    font-style: italic;
    font-size: 0.875rem;
}

/* Rendered markdown: keep block spacing tight enough for a 420px panel. */
.markdown-body :deep(p),
.markdown-body :deep(ul),
.markdown-body :deep(ol),
.markdown-body :deep(pre) {
    margin: 0 0 0.5rem;
}

.markdown-body :deep(:last-child) {
    margin-bottom: 0;
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) {
    margin: 0.75rem 0 0.35rem;
    font-size: 0.9rem;
}

.markdown-body :deep(pre) {
    overflow-x: auto;
    padding: 0.5rem;
    background: var(--p-surface-100);
    border-radius: 4px;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
    padding-left: 1.25rem;
}

.section-title {
    margin: 0;
    font-size: 1rem;
}

/* Reads as a statement of fact rather than a control: this panel shows the
   evidence requirement, the full page sets it. */
.evidence-required {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    font-style: normal;
    opacity: 1;
    color: var(--p-text-secondary-color);
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.field label {
    font-weight: 500;
    font-size: 0.9rem;
}

.field-error {
    color: var(--p-red-500, #ef4444);
    font-size: 0.8rem;
}

.field-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.field-error-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.field-error-block p {
    margin: 0;
}

/* "w-full" isn't a global utility in this app — every component that uses it
   defines its own scoped rule. Without this, PrimeVue's InputText already
   defaults to full width, but Textarea falls back to its native intrinsic
   (cols-based) width. */
.w-full {
    width: 100%;
}

.panel-form-hint {
    margin: 0;
    font-size: 0.8rem;
    font-style: italic;
    text-align: left;
    color: var(--p-text-muted-color);
}
</style>
